import { withStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Grid2 as Grid } from "@mui/material";

import { ENGINE_CHART_TYPE } from "../../../../constants/trendGraph/enginesLine";
import localizationKeys from "../../../../i18n/localizationKeys";
import { setDataDuration, setElectricDataDuration } from "../../../../model/slice/dataDurationSlice";
import dayjs from "../../../../util/dayjs-init.js";
import { DATE_FORMAT } from "../../../../constants/constants";
import { engineSelect } from "../../../../util/monitoring/monitoring.js";

import { EngineTrendGraph } from "../../../trendPage/engine/index";
import { sepDurationInitialState } from "../../../../constants/duration.js";
import { ElectricPanel } from "../../../trendPage/electric/index.jsx";
import { ELECTRIC_TREND_TYPE } from "../../../../constants/trendGraph/electricLines.js";
import { ADDITIONAL_ELECTRIC_TREND_TYPE } from "../../../../constants/trendGraph/additionalElectricTrend.js";
import { DisplayTooltipData } from "./tooltip.jsx";

const styles = (theme) => ({
  container: {
    display: "flex",
    maxHeight: "272px",
    "& .MuiGrid-item": {
      paddingTop: "0px !important",
      paddingLeft: "0px !important",
    },
    "& .MuiPaper-root": {
      height: "240px !important",
      minHeight: "50px !important",
    },
  },
  titleContainer: {
    marginBottom: "16px",
    fontSize: "11px",
  },
  legendContainer: {
    fontSize: "11px",
    marginTop: "16px",
    columnCount: "2",
    columnFill: "auto",
    height: "210px"
  },
  legendLineContainer: {
    display: "flex",
    gap: '8px',
    marginBottom: "8px"
  },
  rectContainer: {
    display: "flex",
    alignItems: "center",
  },
  rect: {
    width: "10px",
    height: "2px",
    flexGrow: 0,
    borderRadius: "1px",
  },
  legend: {
    minWidth: '110px',
    maxWidth: '110px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('lg')]: {
      minWidth: '100px',
      maxWidth: '100px',
    }
  },
  value: {
    width: '50px',
    textAlign: 'right',
  },
});

const Display = ({ vessel, isSepVessel, classes, idx, engine, t }) => {
  const [graphData, setGraphData] = useState();
  const [dataFetchStatus, setDataFetchStatus] = useState({});
  const [chartRef, setChartRef] = useState(null);
  useEffect(() => {
    if (chartRef?.current) {
      chartRef.current.dispose();
    }
    setChartRef(null);
  }, [vessel.id]);

  return (
    <Grid container className={ `${classes.components} ${classes.container}` }>
      <Grid item size={{xs:6}}>
        <div className={classes.titleContainer}>
          {isSepVessel ? `${t(localizationKeys.ElectricData_lower)}` : `${t(localizationKeys.Engine_lower)} ${idx+1} - ${t(localizationKeys.CylinderDeviation_lower)}`} (24 hours)
        </div>
        {
          isSepVessel ?
          <ElectricPanel
            vesselId={vessel.id}
            currentVessel={vessel}
            graphData={graphData}
            setGraphData={setGraphData}
            setDataFetchStatus={setDataFetchStatus}
            selectedChart={isSepVessel ? ADDITIONAL_ELECTRIC_TREND_TYPE.SEP_ELECTRIC_POWER : ELECTRIC_TREND_TYPE.ELECTRIC_POWER_TREND}
            isVesselOverview={true}
            setChartRef={setChartRef}
            setGraphConfig={() => {}}
          /> :
          <EngineTrendGraph
            vesselId={vessel.id}
            currentVessel={vessel}
            selectedChart={ENGINE_CHART_TYPE.ENGINE_DEVIATION}
            selectedEngine={engine.position}
            graphData={graphData}
            setGraphData={setGraphData}
            setDataFetchStatus={setDataFetchStatus}
            isVesselOverview={true}
            setChartRef={setChartRef}
            overviewTrendIdx={idx}
          />
        }
      </Grid>
      <Grid item size={{xs:6}}>
        {t(localizationKeys.CurrentStatus)}:
        {chartRef &&
          <div className={classes.legendContainer}><DisplayTooltipData dataFetchStatus={dataFetchStatus} isSepVessel={isSepVessel} chart={chartRef} classes={classes} /></div>
        }
      </Grid>
    </Grid>
  )
}

const Trend = ({ vessel, isSepVessel, style, classes, t }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (isSepVessel) {
      dispatch(setElectricDataDuration(sepDurationInitialState()));
    } else {
      dispatch(
        setDataDuration({
          startDate: dayjs.utc()
            .subtract(1, "d")
            .format(DATE_FORMAT),
          endDate: dayjs.utc()
            .format(DATE_FORMAT)
        })
      );
    }
  }, [vessel.id, isSepVessel]);

  const engines = engineSelect(t, vessel.NoME);

  return isSepVessel ?
    <Display vessel={vessel} classes={{...style, ...classes}} isSepVessel={isSepVessel} t={t} /> :
    engines.map((engine, i) => (
      <Display vessel={vessel} classes={{...style, ...classes}} idx={i} engine={engine} key={i} t={t} />
    ));
}

Trend.propTypes = {
  vessel: PropTypes.object,
  isSepVessel: PropTypes.bool,
  style: PropTypes.object,
  classes: PropTypes.objectOf(PropTypes.string.isRequired).isRequired,
  t: PropTypes.func.isRequired,
};

Display.propTypes = {
  vessel: PropTypes.object,
  isSepVessel: PropTypes.bool,
  idx: PropTypes.number,
  engine: PropTypes.object,
  classes: PropTypes.objectOf(PropTypes.string.isRequired).isRequired,
  t: PropTypes.func.isRequired,
};

export default withStyles(styles)(Trend);